import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Link2, Trash2, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { API_URL } from "@/lib/api";
const API = `${API_URL}/api`;

const GoogleSheetsSync = ({ onSyncComplete }) => {
  const [configs, setConfigs] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [newConfig, setNewConfig] = useState({ name: "", url: "", target: "inventario", sheet_name: "" });
  const [saving, setSaving] = useState(false);

  const fetchConfigs = async () => {
    try {
      const res = await axios.get(`${API}/sheets/config`);
      setConfigs(res.data.configs);
    } catch {
      // No configs yet
    }
  };

  useEffect(() => { fetchConfigs(); }, []);

  const handleSave = async () => {
    if (!newConfig.name || !newConfig.url) {
      toast.error("Completa nombre y URL");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/sheets/config`, newConfig);
      toast.success("Configuracion guardada");
      setNewConfig({ name: "", url: "", target: "inventario", sheet_name: "" });
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async (target) => {
    setSyncing(target);
    try {
      const res = await axios.post(`${API}/sheets/sync/${target}`);
      toast.success(res.data.message);
      if (onSyncComplete) onSyncComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar");
    } finally {
      setSyncing(null);
    }
  };

  const handleDelete = async (target) => {
    try {
      await axios.delete(`${API}/sheets/config/${target}`);
      toast.success("Configuracion eliminada");
      fetchConfigs();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowDialog(true)}
        className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
        data-testid="sheets-sync-button"
      >
        <Link2 className="w-4 h-4 mr-2" />
        Google Sheets
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-foreground">Sincronizar con Google Sheets</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Existing configs */}
            {configs.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Hojas configuradas</Label>
                {configs.map(c => (
                  <div key={c.target} className="flex items-center gap-2 p-3 bg-muted rounded-lg border" data-testid={`sheet-config-${c.target}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                      {c.target === 'inventario' ? 'Inventario' : 'Salidas'}
                      {c.sheet_name ? ` (${c.sheet_name})` : ''}
                    </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleSync(c.target)}
                      disabled={syncing !== null}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                      data-testid={`sync-${c.target}-button`}
                    >
                      {syncing === c.target ? (
                        <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sincronizando...</>
                      ) : (
                        <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Sincronizar</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(c.target)}
                      className="text-rose-500 hover:bg-rose-50 shrink-0 px-2"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new config */}
            <div className="border-t pt-4 space-y-3">
              <Label className="text-sm font-medium text-foreground">Agregar nueva hoja</Label>
              <Input
                placeholder="Nombre (ej. Inventario Principal)"
                value={newConfig.name}
                onChange={e => setNewConfig(prev => ({ ...prev, name: e.target.value }))}
                data-testid="sheet-name-input"
              />
              <Input
                placeholder="URL de Google Sheets"
                value={newConfig.url}
                onChange={e => setNewConfig(prev => ({ ...prev, url: e.target.value }))}
                data-testid="sheet-url-input"
              />
              <Select value={newConfig.target} onValueChange={v => setNewConfig(prev => ({ ...prev, target: v }))}>
                <SelectTrigger data-testid="sheet-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inventario">Inventario</SelectItem>
                  <SelectItem value="entregas">Salidas</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Nombre de pestaña (ej. SALIDAS EM) - opcional"
                value={newConfig.sheet_name}
                onChange={e => setNewConfig(prev => ({ ...prev, sheet_name: e.target.value }))}
                data-testid="sheet-tab-name-input"
              />
              <Button
                onClick={handleSave}
                disabled={saving || !newConfig.name || !newConfig.url}
                className="w-full bg-[#FF9900] hover:bg-[#E68A00] text-white"
                data-testid="save-sheet-config-button"
              >
                {saving ? "Guardando..." : <><Plus className="w-4 h-4 mr-2" />Guardar configuracion</>}
              </Button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>Requisito:</strong> La hoja debe estar compartida como <strong>"Cualquier persona con el enlace - Lector"</strong> para que el sistema pueda leerla.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GoogleSheetsSync;
